import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

export const APPRAISAL_HELP = {
  values: {
    icon: "bi-gem",
    titleDefault: "What are Values?",
    meaningDefault:
      "Values are your company’s core principles — for an engineering org: Craftsmanship, Collaboration, Ownership.",
    whyDefault:
      "They keep reviews fair and consistent. Engineers are judged against shared engineering culture, not each manager’s personal opinion.",
    exampleDefault: "Example: Craftsmanship, Collaboration, Customer Impact, Continuous Learning.",
    howDefault: "Create Values first. They organise Behaviors, but Values themselves are not scored directly.",
  },
  groups: {
    icon: "bi-collection",
    titleDefault: "What are Behavior Groups?",
    meaningDefault:
      "A Behavior Group is a package of behaviors for a role — e.g. Software Engineering, QA, Platform / DevOps. Different from the Values & Behaviors menu itself, which is where you create them.",
    whyDefault:
      "Backend, frontend, and QA need different checklists without rewriting the whole framework.",
    exampleDefault: "Example: Software Engineering, QA Engineering, Engineering Managers.",
    howDefault:
      "Create groups inside Values & Behaviors. When you add a Behavior, choose which group it belongs to.",
  },
  behaviors: {
    icon: "bi-list-check",
    titleDefault: "What are Behaviors?",
    meaningDefault: "Behaviors are the clear, observable statements people actually rate during an appraisal.",
    whyDefault:
      "They turn engineering culture into measurable performance — clearer feedback, fairer scores, and better pay decisions.",
    exampleDefault:
      "Example under Craftsmanship: “Writes clear PRs with tests and risk notes” or “Leaves code easier to maintain than they found it.”",
    howDefault:
      "Each Behavior is linked to one Value and one Behavior Group. Staff, managers, and meetings score these statements.",
  },
  frameworkOverview: {
    icon: "bi-diagram-3",
    titleDefault: "How Values & Behaviors fit together",
    meaningDefault:
      "Values are the principles. Behavior Groups package expectations by role. Behaviors are the rated statements under a Value + Group link.",
    whyDefault:
      "This structure makes appraisals consistent across engineers and useful for coaching, compliance, and pay recommendations.",
    exampleDefault:
      "Example: Value “Craftsmanship” → Group “Software Engineering” → Behavior “Writes clear PRs with tests.”",
    howDefault:
      "Sidebar setup order: 1) Values & Behaviors, 2) Objectives, 3) Retrospectives. Then start a cycle.",
    steps: [
      { icon: "bi-gem", text: "Create Values (e.g. Craftsmanship)." },
      { icon: "bi-collection", text: "Create Behavior Groups (e.g. Software Engineering)." },
      { icon: "bi-list-check", text: "Add Behaviors linked to a Value + Group." },
      { icon: "bi-flag", text: "Add Objectives (goal templates), then Retrospectives." },
    ],
  },
  retrospective: {
    icon: "bi-arrow-counterclockwise",
    titleDefault: "What is a Retrospective?",
    meaningDefault:
      "A Retrospective is a reflection question about the past period — for example what went well, what blocked progress, or what to do differently next time.",
    whyDefault:
      "Behaviors measure values. Retrospectives capture learning and context, which helps coaching and better next-year goals or PDP items.",
    exampleDefault: "Example: “Reduced tech debt on the auth service” or “Improved CI flakiness this quarter.”",
    howDefault:
      "Admin creates retrospective statements (and optional groups). Enable Retrospective in Settings before starting a cycle. Staff, manager, and meeting then rate those reflections alongside Behaviors. Do not change the Retro flag mid-cycle.",
  },
  retrospectiveGroups: {
    icon: "bi-folder2-open",
    titleDefault: "What are Retrospective Groups?",
    meaningDefault:
      "A Retrospective Group is a package of reflection questions you can reuse — for example Software Engineering Retros.",
    whyDefault: "Different roles may need different reflection prompts. Groups keep that organised and reusable.",
    exampleDefault: "Example: Software Engineering Retros containing 3–5 reflection statements.",
    howDefault: "Create retrospective statements first, then create a group and tick which statements belong to it. Use Delete on the list to remove a statement or group.",
  },
  pdp: {
    icon: "bi-rocket-takeoff",
    titleDefault: "What is a PDP?",
    meaningDefault:
      "A Personal Development Plan (PDP) is a practical growth plan: tasks the employee will do to improve skills or performance.",
    whyDefault:
      "Appraisal finds the gap. PDP turns that gap into tracked actions instead of “talk only” feedback.",
    exampleDefault:
      "Example for an engineer: Complete a system-design course, pair on production incidents for a month, or improve PR review quality by Q3.",
    howDefault:
      "Create a Draft task → submit for approval → manager/admin approves → employee works it → mark Completed. If LMS is enabled, course links appear on My PDP.",
    steps: [
      { icon: "bi-pencil-square", text: "Create a Draft PDP activity." },
      { icon: "bi-send", text: "Submit for manager approval." },
      { icon: "bi-check2-circle", text: "Approve, work the task, then mark Completed." },
    ],
  },
  payIncrease: {
    icon: "bi-cash-coin",
    titleDefault: "What is Pay Increase in Appraisals?",
    meaningDefault:
      "A Pay Increase recommendation after (or near the end of) the appraisal: should this person get a raise?",
    whyDefault:
      "It connects performance evidence to pay decisions, while keeping payroll in control. Appraisal never changes salary automatically.",
    exampleDefault:
      "Manager recommends a new gross salary and effective date. Payroll sees a Pending Salary Increment with Source = Appraisal.",
    howDefault:
      "First set up Payroll → Salary components and publish a Payroll Structure. Then enable Pay increase in Appraisal Settings. When status is Manager Submitted or Meeting Confirmed, manager/admin recommends Yes (new gross + date) or No. Payroll activates the pending increment on/after the effective date.",
    steps: [
      { icon: "bi-wallet2", text: "Create Salary Components and publish a Payroll Structure." },
      { icon: "bi-toggle-on", text: "Turn on Pay increase in Appraisal Settings." },
      { icon: "bi-clipboard-check", text: "Recommend increase on the assessment." },
      { icon: "bi-wallet2", text: "Payroll activates the Pending Salary Increment." },
    ],
  },
  goals: {
    icon: "bi-bullseye",
    titleDefault: "What are Goals?",
    meaningDefault:
      "Goals are agreed outcomes the employee will work toward — usually linked to company Objectives, with milestones, measurements, and optional KPIs.",
    whyDefault:
      "Behaviors rate how someone works. Goals track what they deliver. Together they give a complete performance picture.",
    exampleDefault:
      "Example: “Improve first-response SLA to under 4 hours by September,” with milestones and a KPI for average response time.",
    howDefault:
      "Create a Draft goal → submit for approval → manager approves → employee rates progress → manager agrees or discusses → complete. Team Goals lets managers review their reports’ goals.",
    steps: [
      { icon: "bi-pencil-square", text: "Create a Draft goal (optional Objective template)." },
      { icon: "bi-send", text: "Submit for approval, then manager approves." },
      { icon: "bi-star", text: "Rate progress → manager agrees/discusses → Complete." },
    ],
  },
  objectives: {
    icon: "bi-flag",
    titleDefault: "What are Objectives (Goals)?",
    meaningDefault:
      "Objectives are company goal templates — reusable themes or outcomes the business wants people to aim for (for example Customer Excellence or Operational Efficiency).",
    whyDefault:
      "They keep individual goals aligned to company priorities. Employees don’t invent direction from scratch; they pick from a shared library.",
    exampleDefault:
      "Example Objective: “Improve customer response quality.” Employees then create personal Goals under that template.",
    howDefault:
      "Admin creates Objectives on this page. When employees create Goals, they can optionally select an Objective template. Objective Groups package related objectives for different teams or roles.",
    steps: [
      { icon: "bi-flag", text: "Create Objectives (company goal templates)." },
      { icon: "bi-folder2", text: "Optional: create Objective Groups for teams/roles." },
      { icon: "bi-bullseye", text: "Employees create Goals and can link an Objective." },
    ],
  },
  objectiveGroups: {
    icon: "bi-folder2",
    titleDefault: "What are Objective Groups?",
    meaningDefault:
      "An Objective Group is a package of related objectives — for example Leadership Objectives or Frontline Objectives.",
    whyDefault:
      "Different teams need different goal themes. Groups organise the objective library so the right templates are easy to find and assign.",
    exampleDefault: "Example: “Sales Objectives” group containing pipeline growth and client retention templates.",
    howDefault:
      "Create Objectives first, then create groups to organise them. Groups help structure the catalogue used when people set Goals.",
  },
  evidence: {
    icon: "bi-folder-symlink",
    titleDefault: "What is Evidence?",
    meaningDefault:
      "Evidence is proof of contribution — notes, examples, or file links that support your appraisal, goals, PDP, or retrospectives.",
    whyDefault:
      "It reduces “he said / she said.” Reviews and pay decisions are stronger when claims are backed by real examples.",
    exampleDefault:
      "Example: “Led the Q2 onboarding redesign” with a link to the project summary document.",
    howDefault:
      "Employees add evidence with a headline, contribution notes, and optional file URL. You can mark items completed and remove outdated ones from My Evidence.",
  },
  oneToOne: {
    icon: "bi-people",
    titleDefault: "What are One-to-ones?",
    meaningDefault:
      "One-to-ones are ongoing manager–employee check-ins outside the formal annual appraisal cycle — meeting notes, actions, and follow-ups.",
    whyDefault:
      "Performance conversations should not wait for once-a-year appraisals. Regular 1:1s keep coaching continuous.",
    exampleDefault:
      "Example: Monthly check-in with headline “May progress review,” notes, and agreed next actions.",
    howDefault:
      "Enable One-to-one in Settings. Create a meeting (headline, notes/actions, times), add comments, then mark completed when done. Managers use Team One-to-ones for their reports.",
    steps: [
      { icon: "bi-calendar-plus", text: "Create a one-to-one with notes and times." },
      { icon: "bi-chat-left-text", text: "Add comments during follow-up." },
      { icon: "bi-check2-circle", text: "Mark the meeting completed." },
    ],
  },
  conversations: {
    icon: "bi-chat-square-heart",
    titleDefault: "What are Conversations?",
    meaningDefault:
      "Conversations are structured 1:1 check-ins with topic prompts — wellbeing, performance, motivation, recent work, and housekeeping.",
    whyDefault:
      "They make check-ins consistent and thorough, so important topics are not skipped.",
    exampleDefault:
      "Example: A monthly conversation covering wellbeing, current performance, and housekeeping actions.",
    howDefault:
      "Enable One-to-one conversations in Settings. Create a conversation, fill the topic fields, comment as needed, then complete it. Team Conversations is the manager view.",
    steps: [
      { icon: "bi-list-check", text: "Create a conversation and fill topic sections." },
      { icon: "bi-chat-dots", text: "Add comments if needed." },
      { icon: "bi-check2-all", text: "Mark the conversation completed." },
    ],
  },
  ratingScale: {
    icon: "bi-123",
    titleDefault: "What do the rating numbers mean?",
    meaningDefault:
      "Each number is a score for that behavior or retrospective statement. Higher usually means stronger performance against the statement.",
    whyDefault:
      "A shared scale keeps self, manager, and meeting ratings comparable — and makes averages on Overview meaningful.",
    exampleDefault:
      "On a 1–5 scale: 1 Needs improvement, 3 Meets expectations, 5 Outstanding. Your company can also set custom labels for each number.",
    howDefault:
      "Read the legend above the score buttons, hover a number for its label, then pick the score that best matches the evidence. Add a short comment to explain your choice.",
    steps: [
      { icon: "bi-list-ol", text: "Check the rating legend for this company." },
      { icon: "bi-hand-index", text: "Select the number that best fits the statement." },
      { icon: "bi-chat-left-text", text: "Add a comment with evidence or examples." },
    ],
  },
  settingsOverview: {
    icon: "bi-gear",
    titleDefault: "How Appraisal Settings work",
    meaningDefault:
      "Settings control which appraisal features are on, how often cycles run, rating maxima, and whether you start a cycle for the whole company or one office.",
    whyDefault:
      "Turning features on only when you need them keeps My Appraisal simple for staff, while admins still have full control of cycles and scales.",
    exampleDefault:
      "Example: enable Retrospective + Pay increase + PDP for an annual cycle, set behavior max to 5, then Start appraisal cycle for all offices.",
    howDefault:
      "1) Review Features and open What is this? on each flag. 2) Set Schedule and Rating scales. 3) Save. 4) Start a cycle when you are ready (or wait for the schedule).",
    steps: [
      { icon: "bi-toggles", text: "Enable only the features your company will use." },
      { icon: "bi-calendar3", text: "Configure schedule and rating maxima, then Save." },
      { icon: "bi-play-circle", text: "Start a cycle for all offices or one office." },
    ],
  },
  startCycle: {
    icon: "bi-play-circle",
    titleDefault: "What is Start appraisal cycle?",
    meaningDefault:
      "Starting a cycle creates a live appraisal for each selected employee so they can complete Self → Manager → Meeting.",
    whyDefault:
      "Without a cycle, My Appraisal stays empty. Starting one (or letting the schedule run) is what opens the workflow for the team.",
    exampleDefault:
      "Example: Start for All company offices before annual reviews, or for one office that finishes earlier.",
    howDefault:
      "Choose All offices or a specific office, confirm, then track progress on Appraisal Overview / Compliance. If that scope still has unfinished appraisals, you get a second warning before a new cycle is created.",
  },
  schedule: {
    icon: "bi-calendar3",
    titleDefault: "What is the Appraisal Schedule?",
    meaningDefault:
      "The schedule can automatically start the next cycle after a set number of months, for all offices or one office.",
    whyDefault:
      "It reduces manual admin work for recurring annual or mid-year appraisals.",
    exampleDefault: "Example: Frequency 12 months, all offices — a new company-wide cycle starts about once a year.",
    howDefault:
      "Turn on frequency, set months, choose scope, then Save. Check Last automatic run after the job has executed.",
  },
  ratingScalesSettings: {
    icon: "bi-sliders",
    titleDefault: "What are Rating scales?",
    meaningDefault:
      "These numbers set the maximum score people can pick for behaviors, goals, and retrospectives (for example 1–5).",
    whyDefault:
      "A consistent max score keeps dashboards and comparisons fair across the company.",
    exampleDefault: "Example: Behavior max 5, Goal max 5, Retrospective max 4.",
    howDefault:
      "Set the maxima here and Save. Staff see those ranges (and any custom labels) on My Appraisal and assessments.",
  },
  feedback: {
    icon: "bi-chat-quote",
    titleDefault: "What is Feedback?",
    meaningDefault:
      "Feedback is an optional closing note / outcome on the appraisal after ratings are done.",
    whyDefault:
      "It captures the agreed narrative outcome so the review is not only numbers.",
    exampleDefault: "Example: A short summary of strengths, focus areas, and next steps after the meeting.",
    howDefault:
      "Enable Feedback in Settings when you want that closing field available in the appraisal workflow. While appraisals are still in progress, Feedback cannot be turned off (same rule as Retrospective).",
  },
  meetingConfirm: {
    icon: "bi-people",
    titleDefault: "What is Meeting Confirm?",
    meaningDefault:
      "Step 6 is the joint review meeting where agreed meeting ratings and final comments are recorded, then Confirmed. The line manager (e.g. Mike) completes this — admin does not need to attend.",
    whyDefault:
      "It locks the agreed outcome after Self and Manager ratings, so pay/PDP next steps use a shared final view.",
    exampleDefault:
      "Example: Mike and Sara meet; Mike enters meeting ratings/comments and clicks Confirm. Admin can do the same only if they act with manager/admin access — they are not required in the room.",
    howDefault:
      "Open the assessment at Manager Submitted → enter Meeting ratings + final comments → Confirm. Optional pay recommend can follow after confirm.",
  },
  lms: {
    icon: "bi-mortarboard",
    titleDefault: "What is LMS?",
    meaningDefault:
      "LMS is your company learning catalogue — course name, description, and a link to the external training portal. It appears as its own section on My PDP.",
    whyDefault:
      "Staff can see what training is available (not just a button), then open the course and optionally track it as a PDP activity with their manager.",
    exampleDefault:
      "Example: Course “System design fundamentals” with description “Architecture patterns for service teams” and an Open course link.",
    howDefault:
      "Enable LMS in Settings → add courses under Appraisal → Learning courses (name, description, URL) → employees see the catalogue on My PDP, separate from development activities.",
  },
  features: {
    icon: "bi-toggles",
    titleDefault: "What are Appraisal Features?",
    meaningDefault:
      "Feature flags turn optional building blocks on or off: retrospectives, goals/KPIs, pay increase, PDP, LMS, one-to-ones, and conversations.",
    whyDefault:
      "Not every company needs every module on day one. Flags let you roll out appraisals in stages.",
    exampleDefault:
      "Example: start with Behaviors only, then enable Pay increase and PDP for the next cycle.",
    howDefault:
      "Use What is this? beside each flag, enable what you need, then Save. Disabled features stay hidden from staff menus and forms. While a cycle is incomplete, Retrospective and Feedback cannot be changed (on or off).",
  },
};

export const AppraisalHelpModal = ({ helpKey, onClose, titleId = "appraisal-help-title" }) => {
  const { t } = useTranslation();
  const help = helpKey ? APPRAISAL_HELP[helpKey] : null;

  useEffect(() => {
    if (!help) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [help, onClose]);

  if (!help) return null;

  return (
    <div
      className="appraisal-help-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="appraisal-help-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="appraisal-help-modal__header">
          <span className="appraisal-help-modal__icon" aria-hidden>
            <i className={`bi ${help.icon}`} />
          </span>
          <div>
            <h2 id={titleId}>
              {t(`app.appraisal.help.${helpKey}.title`, { defaultValue: help.titleDefault })}
            </h2>
            <p className="appraisal-help-modal__subtitle">
              {t("app.appraisal.help.popupSubtitle", {
                defaultValue: "Quick guide so your team understands this correctly.",
              })}
            </p>
          </div>
          <button
            type="button"
            className="appraisal-help-modal__close"
            onClick={onClose}
            aria-label={t("app.appraisal.help.close", { defaultValue: "Close" })}
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="appraisal-help-modal__body">
          <div className="appraisal-help-block">
            <h3>{t("app.appraisal.help.meaning", { defaultValue: "Meaning" })}</h3>
            <p>{t(`app.appraisal.help.${helpKey}.meaning`, { defaultValue: help.meaningDefault })}</p>
          </div>
          <div className="appraisal-help-block">
            <h3>{t("app.appraisal.help.whyUseful", { defaultValue: "Why it is useful" })}</h3>
            <p>{t(`app.appraisal.help.${helpKey}.why`, { defaultValue: help.whyDefault })}</p>
          </div>
          <div className="appraisal-help-block">
            <h3>{t("app.appraisal.help.example", { defaultValue: "Example" })}</h3>
            <p>{t(`app.appraisal.help.${helpKey}.example`, { defaultValue: help.exampleDefault })}</p>
          </div>
          <div className="appraisal-help-block">
            <h3>{t("app.appraisal.help.howItWorks", { defaultValue: "How it works here" })}</h3>
            <p>{t(`app.appraisal.help.${helpKey}.how`, { defaultValue: help.howDefault })}</p>
          </div>

          {Array.isArray(help.steps) && help.steps.length > 0 ? (
            <ol className="appraisal-help-steps">
              {help.steps.map((step, index) => (
                <li key={`${helpKey}-step-${index}`}>
                  <i className={`bi ${step.icon}`} aria-hidden />
                  <span>
                    {t(`app.appraisal.help.${helpKey}.steps.${index}`, {
                      defaultValue: step.text,
                    })}
                  </span>
                </li>
              ))}
            </ol>
          ) : null}
        </div>

        <div className="appraisal-help-modal__footer">
          <button type="button" className="appraisal-btn primary" onClick={onClose}>
            {t("app.appraisal.help.gotIt", { defaultValue: "Got it" })}
          </button>
        </div>
      </div>
    </div>
  );
};

export const AppraisalSectionHeader = ({
  icon,
  title,
  helpKey,
  onOpenHelp,
  tone = "overview",
}) => {
  const { t } = useTranslation();
  return (
    <div className="appraisal-framework-section-head">
      <div className="appraisal-framework-section-head__title">
        <span className={`appraisal-framework-section-icon appraisal-framework-section-icon--${tone}`} aria-hidden>
          <i className={`bi ${icon}`} />
        </span>
        <h3>{title}</h3>
      </div>
      {helpKey && onOpenHelp ? (
        <button
          type="button"
          className="appraisal-framework-info-btn"
          onClick={() => onOpenHelp(helpKey)}
          aria-label={t("app.appraisal.help.openAria", {
            defaultValue: "Learn more about {{title}}",
            title,
          })}
          title={t("app.appraisal.help.openTitle", { defaultValue: "What is this?" })}
        >
          <i className="bi bi-info-circle" aria-hidden />
          <span>{t("app.appraisal.help.learn", { defaultValue: "What is this?" })}</span>
        </button>
      ) : null}
    </div>
  );
};

export const AppraisalGuideButton = ({ onClick, label }) => {
  const { t } = useTranslation();
  return (
    <button type="button" className="appraisal-framework-guide-btn" onClick={onClick}>
      <i className="bi bi-info-circle" aria-hidden />
      {label || t("app.appraisal.help.guideBtn", { defaultValue: "How this works" })}
    </button>
  );
};
